import { useQuery, type QueryFunctionContext } from "@tanstack/react-query";
import DataService from "features/Application/services/dataServices/typedDataService";
import { type PerformanceWithAccountFilter, lineChartFactory } from "../../../Common/utils/performanceUtils";
import { reportingColors } from "../../../Common/utils/constants";
import { type DataPoint } from "../../../../../components/charts/types/HorizontalBarChart";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "../../../../../components/cards/CardReporting/CardReporting.module.scss";
import { faSquareArrowDown, faSquareExclamation, faSquareArrowUp } from "@fortawesome/pro-solid-svg-icons";
import { CHART_PERIOD } from "hooks/useChartPeriodMeasure";
import { sharedAccountReportingLineProps } from "features/Reporting/Content/shared";
import { getDateFormatByCount, type GroupedBarProps, type LinechartProps } from "components/charts";
import moment from "moment";
import { sharedGoogleReportingBarProps } from "features/Reporting/Google/utils/shared";
import { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { roundToTwoDigits } from "features/Library/Common/utils/roundToTwoDigits";

type FlowPriorityQueryFunctionContext = QueryFunctionContext<
  [_name: string, filter: PerformanceWithAccountFilter, flowId?: number]
>;

type FlowPriorityQueryRequiredFunctionContext = QueryFunctionContext<
  [_name: string, filter: PerformanceWithAccountFilter, flowId: number]
>;

export type FlowPriorityBarChartData = {
  Completed: number;
  InCompleted: number;
  InProgress: number;
  NotStarted: number;
};

export type FlowPriorityLineChartData = FlowPriorityBarChartData & {
  Date: string;
};

export type FlowPriorityCardsData = {
  TotalImportantFlows: number;
  TotalPrioritizedFlows: number;
  TotalPrioritizedUsers: number;
  TotalRecommendedFlows: number;
  TotalRequiredFlows: number;
};

export type FlowPriorityPeopleData = {
  AccountId: number;
  Email: string;
  FirstName: string;
  LastActivity: string | null;
  LastName: string;
  Priority: number;
  Status: "NotStarted" | "InProgress" | "Completed";
  UserId: number;
};

export type FlowPriorityFlowData = {
  Completed: number;
  FlowId: number;
  FlowName: string;
  FlowPriority: number;
  InProgress: number;
  NotStarted: number;
  TotalUsers: number;
};

export const colorRange = [
  reportingColors.greyLight,
  reportingColors.greyLighter,
  reportingColors.yellow,
  reportingColors.greenLight,
];

export const generatePrioritizedFlowStatistics = (
  data: FlowPriorityCardsData,
  flowId?: number,
): CardReportingItem[] => {
  const individualFlowTooltipTexts = {
    prioritizedUsers: "Total prioritized users in this Flow during the specified time period.",
    requiredFlows: "Total users with required priority in this Flow during the specified time period.",
    importantFlows: "Total users with important priority in this Flow during the specified time period.",
    recommendedFlows: "Total users with recommended priority in this Flow during the specified time period.",
  };

  const contentFlowsTooltipTexts = {
    prioritizedFlows: "Total prioritized Flows during the specified time period.",
    prioritizedUsers: "Total prioritized users across all Flows during the specified time period.",
    requiredFlows: "Total users with required priority across all Flows during the specified time period.",
    importantFlows: "Total users with important priority across all Flows during the specified time period.",
    recommendedFlows: "Total users with recommended priority across all Flows during the specified time period.",
  };

  const getPopupBody = (key: keyof typeof individualFlowTooltipTexts & keyof typeof contentFlowsTooltipTexts) =>
    flowId ? individualFlowTooltipTexts[key] : contentFlowsTooltipTexts[key];

  const cards: CardReportingItem[] = [
    {
      statDescription: "Prioritized Users",
      stat: data.TotalPrioritizedUsers,
      popupBody: getPopupBody("prioritizedUsers"),
    },
    {
      statDescription: (
        <span className={styles.priorityDescription}>
          <FontAwesomeIcon icon={faSquareExclamation} />
          <span>Required</span>
        </span>
      ),
      stat: data.TotalRequiredFlows,
      popupBody: getPopupBody("requiredFlows"),
      key: "requiredFlows",
    },
    {
      statDescription: (
        <span className={styles.priorityDescription}>
          <FontAwesomeIcon icon={faSquareArrowUp} />
          <span>Important</span>
        </span>
      ),
      stat: data.TotalImportantFlows,
      popupBody: getPopupBody("importantFlows"),
      key: "importantFlows",
    },
    {
      statDescription: (
        <span className={styles.priorityDescription}>
          <FontAwesomeIcon icon={faSquareArrowDown} />
          <span>Recommended</span>
        </span>
      ),
      stat: data.TotalRecommendedFlows,
      popupBody: getPopupBody("recommendedFlows"),
      key: "recommendedFlows",
    },
  ];

  if (!flowId) {
    cards.unshift({
      statDescription: "Prioritized Flows",
      stat: data.TotalPrioritizedFlows,
      popupBody: contentFlowsTooltipTexts.prioritizedFlows,
    });
  }

  return cards;
};

const getPriorityLabel = (priority: number) => {
  switch (priority) {
    case 1:
      return "Required";
    case 2:
      return "Important";
    case 3:
      return "Recommended";
    default:
      return "Recommended";
  }
};

const getPriorityIcon = (priority: number) => {
  switch (priority) {
    case 1: // Required
      return faSquareExclamation;
    case 2: // Important
      return faSquareArrowUp;
    case 3: // Recommended
      return faSquareArrowDown;
    default:
      return faSquareArrowDown;
  }
};

export const getFlowPriorityLineChartQuery = async ({
  queryKey,
  signal,
}: FlowPriorityQueryFunctionContext): Promise<{
  lineProps: LinechartProps;
  barProps: GroupedBarProps<{ inCompleted: number; notStarted: number; inProgress: number; completed: number }>;
}> => {
  const [, flowId, filter] = queryKey;
  const { data } = await DataService.flows.getFlowPriorityLineChart(flowId, signal!, filter);

  const lineData = lineChartFactory(data);

  const barData = data.map((d) => ({
    group: moment(d.Date, "YYYY-MM-DD").format("YYYY-MM-DD"),
    inCompleted: d.InCompleted,
    notStarted: d.NotStarted,
    inProgress: d.InProgress,
    completed: d.Completed,
  }));

  return {
    lineProps: {
      ...sharedAccountReportingLineProps,
      xData: [lineData.Date, lineData.Date, lineData.Date, lineData.Date],
      yData: [lineData.InCompleted, lineData.NotStarted, lineData.InProgress, lineData.Completed],
      xFormatterFunc: getDateFormatByCount(lineData?.Date?.length),
      colorRange,
      tooltipFormatter: (_x, y) => roundToTwoDigits(y),
    },
    barProps: {
      ...sharedGoogleReportingBarProps,
      data: barData,
      order: ["inCompleted", "notStarted", "inProgress", "completed"],
      colorRange,
    },
  };
};

export const getFlowPriorityBarChartQuery = async ({ queryKey, signal }: FlowPriorityQueryFunctionContext) => {
  const [, filter, flowId] = queryKey;
  const { data } = await DataService.flows.getFlowPriorityBarChart(filter, signal!, flowId);

  const barData: DataPoint[] = [
    {
      id: "incomplete",
      category: "Incomplete",
      value: data.InCompleted,
      fillColor: reportingColors.greyLight,
    },
    {
      id: "notStarted",
      category: "Not Started",
      value: data.NotStarted,
      fillColor: reportingColors.greyLighter,
    },
    {
      id: "started",
      category: "Started",
      value: data.InProgress,
      fillColor: reportingColors.yellow,
    },
    {
      id: "completed",
      category: "Completed",
      value: data.Completed,
      fillColor: reportingColors.greenLight,
    },
  ];

  const maxValue = barData.reduce((max, d) => Math.max(max, d.value), 0);
  const barDomain = [0, Math.max(1, maxValue)];

  return { barData, barDomain };
};

export const getFlowPriorityCardsQuery = async ({ queryKey, signal }: FlowPriorityQueryFunctionContext) => {
  const [, filter, flowId] = queryKey;
  const { data } = await DataService.flows.getFlowPriorityCards(filter, signal!, flowId);
  return generatePrioritizedFlowStatistics(data, flowId);
};

export const getFlowPriorityPeopleDetailsQuery = async ({
  queryKey,
  signal,
}: FlowPriorityQueryRequiredFunctionContext): Promise<FlowPriorityPeopleData[]> => {
  const [, filter, flowId] = queryKey;
  const { data } = await DataService.flows.getFlowPriorityPeopleDetails(filter, signal!, flowId);
  return data.map((person: FlowPriorityPeopleData) => ({
    ...person,
    PriorityIcon: getPriorityIcon(person.Priority),
    PriorityLabel: getPriorityLabel(person.Priority),
    id: person.UserId,
  }));
};

export const getFlowPriorityFlowDetailsQuery = async ({
  queryKey,
  signal,
}: FlowPriorityQueryFunctionContext): Promise<FlowPriorityFlowData[]> => {
  const [, filter] = queryKey;
  const { data } = await DataService.flows.getFlowPriorityFlowDetails(filter, signal!);
  return data.map((flow: FlowPriorityFlowData) => ({
    ...flow,
    PriorityIcon: getPriorityIcon(flow.FlowPriority),
    PriorityLabel: getPriorityLabel(flow.FlowPriority),
  }));
};

export const useFlowPriorityLineChartQuery = (filter: PerformanceWithAccountFilter, flowId?: number) => {
  return useQuery({
    queryKey: ["flowPriorityLineChart", filter, flowId],
    queryFn: getFlowPriorityLineChartQuery,
    enabled: filter.type !== undefined && filter.aggregation !== CHART_PERIOD.UNSET,
  });
};

export const useFlowPriorityBarChartQuery = (filter: PerformanceWithAccountFilter, flowId?: number) => {
  return useQuery({
    queryKey: ["flowPriorityBarChart", filter, flowId],
    queryFn: getFlowPriorityBarChartQuery,
    enabled: filter.type !== undefined,
  });
};

export const useFlowPriorityCardsQuery = (filter: PerformanceWithAccountFilter, flowId?: number) => {
  return useQuery({
    queryKey: ["flowPriorityCards", filter, flowId],
    queryFn: getFlowPriorityCardsQuery,
    enabled: filter.type !== undefined,
  });
};

export const useFlowPriorityPeopleDetailsQuery = (filter: PerformanceWithAccountFilter, flowId?: number) => {
  return useQuery({
    queryKey: ["flowPriorityPeopleDetails", filter, flowId!],
    queryFn: getFlowPriorityPeopleDetailsQuery,
    enabled: filter.type !== undefined && flowId !== undefined,
  });
};

export const useFlowPriorityFlowDetailsQuery = (filter: PerformanceWithAccountFilter, flowId?: number) => {
  return useQuery({
    queryKey: ["flowPriorityFlowDetails", filter],
    queryFn: getFlowPriorityFlowDetailsQuery,
    enabled: filter.type !== undefined && flowId === undefined,
  });
};
